import { useEffect, useState } from "react";
import { RESTAURANT_THEME_STYLE_ID } from "@core/tenant/useRestaurantTheme";

/**
 * Convert a Tailwind/shadcn HSL CSS-variable string (e.g. "0 0% 95%") to a
 * 6-digit lowercase hex string. Exported for testing.
 *
 * Returns `#000000` for malformed input — callers should validate by reading
 * the result, not by checking for an exception.
 */
/**
 * Mix two hex colors at the given ratio (0 = fully `bg`, 1 = fully `fg`).
 * Mirrors `color-mix(in srgb, fg X%, bg)` semantics. Used to flatten an
 * RGBA-style translucent placeholder tone into a single opaque hex —
 * Stripe's CardElement doesn't accept colors with alpha.
 *
 * Why this matters: the HTML inputs in the surrounding form (cardholder
 * name, billing address) render their placeholders via browser default,
 * which is `currentColor` × ~50% opacity over the card surface. The
 * Stripe iframe used to take `--muted-foreground` directly, which is a
 * discrete grayer hex that renders much MORE OPAQUE than the
 * translucent HTML placeholders. The visual mismatch flagged it as
 * inconsistent. This helper lets us pre-bake the same effective tone.
 */
export function mixHex(fg: string, bg: string, ratio: number): string {
  const parse = (hex: string) => {
    const h = hex.replace("#", "");
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  };
  const [fr, fgRgb, fb] = parse(fg);
  const [br, bgRgb, bb] = parse(bg);
  const mix = (a: number, b: number) =>
    Math.round(a * ratio + b * (1 - ratio))
      .toString(16)
      .padStart(2, "0");
  return "#" + mix(fr, br) + mix(fgRgb, bgRgb) + mix(fb, bb);
}

export function hslVarToHex(hslVar: string): string {
  const parts = hslVar.trim().split(/\s+/);
  if (parts.length !== 3) return "#000000";
  const h = parseFloat(parts[0]);
  const s = parseFloat(parts[1]) / 100;
  const l = parseFloat(parts[2]) / 100;
  if (!Number.isFinite(h) || !Number.isFinite(s) || !Number.isFinite(l)) {
    return "#000000";
  }

  // Standard HSL→RGB conversion (CSS Color Module Level 3).
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }

  const toByte = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return "#" + toByte(r) + toByte(g) + toByte(b);
}

/**
 * Probe the actual rendered `::placeholder` color the browser applies to a
 * plain HTML input in the current theme. Stripe's iframe can't read CSS
 * variables OR observe Tailwind's preflight, so to make its placeholder
 * match the surrounding HTML inputs (cardholder name, billing address)
 * we have to render a throwaway probe element in the live DOM, read its
 * computed `::placeholder` color, and pass that exact hex to Stripe.
 *
 * Earlier attempts derived this from `--muted-foreground` (rendered too
 * opaque on dark themes) and from `mixHex(--card-foreground, --card, 0.4)`
 * (overcorrected the other way — Stripe placeholder ended up MORE faded
 * than the HTML inputs). Both were eyeballed approximations that
 * inevitably mismatched on at least one tenant theme. This is the only
 * approach that is provably correct: it returns whatever the browser
 * actually shows for an HTML input on this tenant's active CSS.
 */
function probeBrowserPlaceholderColor(): string | null {
  if (typeof window === "undefined" || typeof document === "undefined") return null;
  const probe = document.createElement("input");
  probe.type = "text";
  probe.placeholder = "probe";
  // Position offscreen, no visual side effect.
  probe.style.position = "absolute";
  probe.style.left = "-9999px";
  probe.style.top = "-9999px";
  probe.style.opacity = "0";
  probe.style.pointerEvents = "none";
  probe.setAttribute("aria-hidden", "true");
  document.body.appendChild(probe);
  try {
    const ph = getComputedStyle(probe, "::placeholder");
    // Capture optional alpha — rgba()/rgb() with slash-syntax can produce
    // a fractional fourth channel. Tailwind v3 preflight emits opaque
    // colors today, but a tenant theme override (or a CSS reset that uses
    // `currentColor` with opacity) can return an rgba<1 here. Dropping the
    // alpha silently flattens to an opaque hex and reintroduces the v1
    // "too opaque vs HTML inputs" mismatch — caught in PR #231 MEDIUM.
    // Accept both legacy comma-separated `rgb(R, G, B[, A])` AND CSS Color
    // Level 4 space-separated `rgb(R G B[ / A])`. Modern Chrome/Safari emit
    // either form depending on the source declaration. `[\s,]+` between
    // channels handles both; the alpha separator `[,/]` covers both legacy
    // comma alpha and L4 slash alpha.
    const m = ph.color.match(
      /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:\s*[,/]\s*([0-9.]+))?/,
    );
    if (!m) return null;
    const r = +m[1];
    const g = +m[2];
    const b = +m[3];
    const a = m[4] !== undefined ? parseFloat(m[4]) : 1;
    const toHex = (n: number) => Math.round(n).toString(16).padStart(2, "0");
    if (a >= 1) {
      return "#" + toHex(r) + toHex(g) + toHex(b);
    }
    // Translucent placeholder: flatten against the surface the iframe sits
    // on (--card). Stripe's CardElement accepts only opaque hex, so we
    // pre-compose the rgba over the card background here.
    const cardVar = getComputedStyle(document.documentElement)
      .getPropertyValue("--card")
      .trim();
    const cardHex = cardVar ? hslVarToHex(cardVar) : "#333333";
    const cardR = parseInt(cardHex.slice(1, 3), 16);
    const cardG = parseInt(cardHex.slice(3, 5), 16);
    const cardB = parseInt(cardHex.slice(5, 7), 16);
    return (
      "#" +
      toHex(r * a + cardR * (1 - a)) +
      toHex(g * a + cardG * (1 - a)) +
      toHex(b * a + cardB * (1 - a))
    );
  } finally {
    probe.remove();
  }
}

/**
 * Returns CardElement style config resolved against the active theme.
 *
 * Stripe's iframe can't read CSS variables, so we resolve `--card-foreground`
 * and `--destructive` ourselves and pass exact hex strings; the placeholder
 * comes from `probeBrowserPlaceholderColor()` above. Whatever the design
 * system says these tokens should be, Stripe matches them — no binary
 * classifier, no hardcoded brand colours.
 *
 * `--card-foreground` (rather than `--foreground`) because Stripe text sits
 * on the card surface; `--card-foreground` is the token explicitly designed
 * to contrast with `--card`, regardless of how the page-level `--foreground`
 * is set.
 *
 * Call from a component, NOT at module load — the DOM must exist. Components
 * use `useCardElementOptions()` below rather than calling this directly, so
 * that a tenant theme injected after render still reaches the iframe.
 */
export function getCardElementOptions() {
  // Defaults for SSR or pre-DOM contexts only. Unreachable in a browser:
  // index.css always defines --card-foreground. Note this value collides with
  // a dark tenant's foreground, so a test asserting it proves nothing unless
  // it loads the index.css default first.
  let color = "#f2f2f2";
  let invalidColor = "#e54040";
  // Default placeholder if probing fails (SSR, jsdom, etc.). Tailwind's
  // gray-400 — a reasonable mid-gray that contrasts on both light and dark
  // surfaces. Real renders use the probed value below.
  let placeholderColor = "#9ca3af";

  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const styles = getComputedStyle(document.documentElement);
    const cardFg = styles.getPropertyValue("--card-foreground").trim();
    const destructive = styles.getPropertyValue("--destructive").trim();
    if (cardFg) color = hslVarToHex(cardFg);
    if (destructive) invalidColor = hslVarToHex(destructive);

    // Programmatic placeholder match: read the browser's computed
    // `::placeholder` color from a probe HTML input. Whatever the active
    // theme + CSS reset produces for an `<input>`'s placeholder, Stripe's
    // iframe uses the identical hex. Tenant theme changes reach the live
    // element via useCardElementOptions below.
    const probed = probeBrowserPlaceholderColor();
    if (probed) placeholderColor = probed;
  }

  return {
    style: {
      base: {
        fontSize: "16px",
        fontFamily: "Inter, sans-serif",
        color,
        "::placeholder": { color: placeholderColor },
      },
      invalid: {
        color: invalidColor,
      },
    },
    hidePostalCode: true,
    disableLink: true,
  };
}

/**
 * React hook wrapper around `getCardElementOptions` that resolves the timing
 * race against `useRestaurantTheme`.
 *
 * `useRestaurantTheme` injects the tenant `<style id="restaurant-theme">` from
 * a `useEffect` in a parent provider, so a plain
 * `useMemo(() => getCardElementOptions(), [])` in a payment component can
 * resolve colors before that `<style>` exists and read the index.css defaults;
 * with an empty dependency array they are then never recomputed.
 *
 * Whether it bites depends on which async source settles last. `TenantGuard`
 * renders pages only once the restaurant query resolves, and the theme config
 * derives from that same query — so on a direct load or refresh of a payment
 * route the page mounts in the same commit that makes the config available,
 * and the child's render beats the parent's injection effect. When the theme
 * lands first (internal navigation) even a one-shot read is correct. Both
 * orderings are pinned in stripe.themeRace.test.tsx.
 *
 * This hook re-resolves once the tenant `<style>` lands in `<head>` — either
 * immediately (it is already present on an internal navigation) or via a
 * `MutationObserver`. Tenants on the default theme never inject the element,
 * so the initial value is final.
 */
export function useCardElementOptions() {
  const [options, setOptions] = useState(() => getCardElementOptions());

  useEffect(() => {
    if (document.getElementById(RESTAURANT_THEME_STYLE_ID)) {
      setOptions(getCardElementOptions());
      return;
    }

    const observer = new MutationObserver(() => {
      if (document.getElementById(RESTAURANT_THEME_STYLE_ID)) {
        setOptions(getCardElementOptions());
        observer.disconnect();
      }
    });
    observer.observe(document.head, { childList: true });
    return () => observer.disconnect();
  }, []);

  return options;
}
